import type { RecordOf } from 'immutable';
import { Map as ImmutableMap, Record } from 'immutable';

import type { QcsSyncResourceStore, QcsSyncResourceStoreProps } from 'app-components/flux/core/resourceStoreTypes';

export const PROFILE_REDUCER_KEY = 'profileStore';

//  =========   ADDRESS  ==========
export type AddressProps = {
  id?: string;
  clientId?: string;

  isDeleted?: boolean;

  createdAt?: string;
  modifiedAt?: string;

  fullName?: string;
  line1?: string;
  line2?: string;
  city?: string;
  state?: string;
  country?: string;
  zipCode?: string;
  defaultForBilling?: boolean;
  defaultForShipping?: boolean;
  defaultForIdentity?: boolean;
  primary?: boolean;
  authUse?: boolean;
  verified?: boolean;
  userProfileId?: string;

  // maybe needed?
  // jobInfo?: any;
  // clientIdFromEntityClientId: string;
};
export type Address = RecordOf<AddressProps>;

export const makeAddress = Record<AddressProps>({
  id: undefined,
  clientId: undefined,

  isDeleted: undefined,

  createdAt: undefined,
  modifiedAt: undefined,

  fullName: '',
  line1: undefined,
  line2: undefined,
  city: undefined,
  state: undefined,
  country: undefined,
  zipCode: undefined,
  defaultForBilling: undefined,
  defaultForShipping: undefined,
  primary: undefined,
  authUse: undefined,
  verified: undefined,
  userProfileId: undefined,
});

//  =========   PHONE  ==========
type PhoneProps = {
  authUse?: boolean;
  createdAt?: string;
  id?: string;
  modifiedAt?: string;
  number?: string;
  primary?: boolean;
  type?: string;
  verified?: boolean;
};
export type Phone = RecordOf<PhoneProps>;
export const makePhone = Record<PhoneProps>({
  authUse: undefined,
  createdAt: undefined,
  id: undefined,
  modifiedAt: undefined,
  number: undefined,
  primary: undefined,
  type: undefined,
  verified: undefined,
});

//  =========   EMAIL  ==========
type EmailProps = {
  address?: string;
  authUse?: boolean;
  createdAt?: string;
  emailCommSourceType?: string;
  emailCommType?: string;
  id?: string;
  modifiedAt?: string;
  primary?: boolean;
  verified?: boolean;
};
export type Email = RecordOf<EmailProps>;
export const makeEmail = Record<EmailProps>({
  address: undefined,
  authUse: undefined,
  createdAt: undefined,
  emailCommSourceType: undefined,
  emailCommType: undefined,
  id: undefined,
  modifiedAt: undefined,
  primary: undefined,
  verified: undefined,
});

//  =========   Profile  ==========
export type ProfileProps = {
  id?: string;
  clientId?: string;

  isDeleted?: boolean;

  createdAt?: string;
  modifiedAt?: string;

  userId?: string;
  username?: string;
  firstName?: string;
  lastName?: string;
  companyName?: string;

  primaryAddress?: Address | AddressProps;
  primaryPhone?: Phone | PhoneProps;
  primaryEmail?: Email | EmailProps;
};
export type ProfilePropsImmu = Omit<ProfileProps, 'primaryAddress' | 'primaryPhone' | 'primaryEmail'> & {
  primaryAddress?: Address;
  primaryPhone?: Phone;
  primaryEmail?: Email;
};

export type Profile = RecordOf<ProfilePropsImmu>;

export const makeProfile = Record<ProfilePropsImmu>({
  id: undefined,
  clientId: undefined,

  isDeleted: undefined,

  createdAt: undefined,
  modifiedAt: undefined,

  userId: undefined,
  username: undefined,
  firstName: undefined,
  lastName: undefined,
  companyName: undefined,

  primaryAddress: undefined,
  primaryPhone: undefined,
  primaryEmail: undefined,
});

// Our store is generated by mkQcsSyncResourceStore in the reducer - it uses the default resourcesById format,
// even though we have only a single profile. This lets us leverage the same functionality and whatever future changes
// we make to our default redux format.
// -------
// export const ProfileStore = Record({
// });

export const makeProfileFull = (obj: ProfileProps | Profile): Profile =>
  makeProfile({
    ...('toObject' in obj ? obj.toObject() : obj),
    primaryAddress: obj.primaryAddress && makeAddress(obj.primaryAddress),
    primaryPhone: obj.primaryPhone && makePhone(obj.primaryPhone),
    primaryEmail: obj.primaryEmail && makeEmail(obj.primaryEmail),
  });

export type StatusType = 'PASSED' | 'FAILED' | 'PENDING' | 'UNKNOWN' | undefined;

export interface ProfileStoreProps extends QcsSyncResourceStoreProps<ProfilePropsImmu> {
  verified?: boolean;
  verificationAttempts?: number;
  verificationThreshold?: number;
  status?: StatusType;
  message?: string;
}

export type ProfileStore = QcsSyncResourceStore<ProfilePropsImmu, ProfileStoreProps>;

export const mkProfileStore = Record<ProfileStoreProps>({
  resourcesById: ImmutableMap<string, Profile>(),

  resourceName: 'profile',
  lastSyncDate: null,
  loadPending: true,
  isLoading: false,
  error: undefined,

  // make sure these stay undefined on the maker, so they don't get overwritten by profile updates
  verified: undefined,
  verificationAttempts: undefined,
  verificationThreshold: undefined, // max number of attempts
  status: undefined,
  message: undefined,
});

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [PROFILE_REDUCER_KEY]: ProfileStore;
  }
}

export type verifyPayload = {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  email: string;
  socialSecurityNumber: string; // only last 4 digits
  telephone: string;
  street: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
};

export type verifyIdentityResponse = {
  status: StatusType;
  message: string;
  verificationThreshold: number;
  callCount: number;
};

export type getVerifyStatusResponse = {
  success: boolean;
  status: StatusType;
  verificationThreshold?: number;
  callCount?: number;
};
